<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'

import { apiRequest } from '../lib/apiClient'
import { useAuthStore } from '../stores/auth'
import { useLoginModalStore } from '../stores/loginModal'
import { usePaymentModalStore } from '../stores/paymentModal'
import { workbenchPath } from './workbenchPaths'
import { WORKBENCH_DEFAULT_INVITATION_CODE } from './workbenchConfig'
const props = defineProps<{
  breadcrumb: string[]
  onRecharge?: () => void
}>()

const auth = useAuthStore()
const loginModal = useLoginModalStore()
const paymentModal = usePaymentModalStore()
const broadcastText = ref('')
const callToActionName = ref('')
const callToActionUrl = ref('')
const showBroadcast = computed(() => Boolean(String(broadcastText.value || '').trim()))
const showCallToAction = computed(() => {
  const name = String(callToActionName.value || '').trim()
  const url = String(callToActionUrl.value || '').trim()
  return Boolean(name && url)
})
const buyCreditsText = computed(() => {
  if (!auth.isLoggedIn) return '未登陆'
  const c = typeof auth.me?.credits === 'number' ? auth.me.credits : 0
  return String(c)
})
const warrantCreditsText = computed(() => {
  if (!auth.isLoggedIn) return '0'
  const c = typeof (auth.me as any)?.warrant === 'number' ? (auth.me as any).warrant : 0
  return String(c)
})
const showWarrantCredits = computed(() => {
  if (!auth.isLoggedIn) return false
  const w = typeof (auth.me as any)?.warrant === 'number' ? (auth.me as any).warrant : 0
  return w > 0
})
function onRecharge() {
  const token = String((auth as any).token || '').trim()
  const exp = Number((auth as any).tokenExpiresAt || 0)
  const valid = Boolean(token) && (!exp || exp > Date.now() + 10_000)
  if (!valid) {
    loginModal.show({ tab: 'sms', nextPath: workbenchPath('/tools'), defaultInvitationCode: WORKBENCH_DEFAULT_INVITATION_CODE })
    return
  }
  void paymentModal.pay({ entry: 'manual', from: 'topbar' })
}

async function fetchBroadcast() {
  try {
    const token = String((auth as any).token || '').trim()
    if (!token) {
      broadcastText.value = ''
      callToActionName.value = ''
      callToActionUrl.value = ''
      return
    }
    const resp = await apiRequest<{
      broadcast?: string
      call_to_action_name?: string
      call_to_action?: string
    }>('/preset/broadcast', { method: 'GET', token, authExpiredMode: 'silent' })
    if (!resp || resp.code !== 0 || !resp.data) {
      broadcastText.value = ''
      callToActionName.value = ''
      callToActionUrl.value = ''
      return
    }
    const bc = String(resp.data.broadcast || '').trim()
    if (!bc) {
      broadcastText.value = ''
      callToActionName.value = ''
      callToActionUrl.value = ''
      return
    }
    broadcastText.value = bc
    callToActionName.value = String(resp.data.call_to_action_name || '').trim()
    callToActionUrl.value = String(resp.data.call_to_action || '').trim()
  } catch {
    broadcastText.value = ''
    callToActionName.value = ''
    callToActionUrl.value = ''
  }
}

function onCallToAction() {
  const url = String(callToActionUrl.value || '').trim()
  if (!url) return
  window.open(url, '_blank', 'noopener,noreferrer')
}

function withBase(p: string) {
  return `${import.meta.env.BASE_URL || '/'}${String(p || '').replace(/^\//, '')}`
}

function assetIconStyle(name: string) {
  const url = withBase(`/assets/${String(name || '').trim()}.svg`)
  return {
    WebkitMaskImage: `url(${url})`,
    maskImage: `url(${url})`,
    WebkitMaskRepeat: 'no-repeat',
    maskRepeat: 'no-repeat',
    WebkitMaskSize: 'contain',
    maskSize: 'contain',
    WebkitMaskPosition: 'center',
    maskPosition: 'center',
    backgroundColor: 'currentColor',
  } as any
}

onMounted(() => {
  void fetchBroadcast()
})
</script>

<template>
  <header
    class="relative flex justify-between items-center w-full px-8 py-4 bg-white/6 backdrop-blur-xl sticky top-0 z-50"
    role="banner"
  >
    <div class="flex items-center gap-4 min-w-0">
      <div class="min-w-0">
        <nav class="flex items-center gap-2 min-w-0" aria-label="Breadcrumb">
          <div v-if="props.breadcrumb.length === 0" class="text-xl font-black text-on-surface truncate font-manrope"></div>
          <div v-if="props.breadcrumb.length === 1" class="text-xl font-black text-on-surface truncate font-manrope">
            {{ props.breadcrumb[0] }}
          </div>
          <template v-else>
            <div class="text-sm font-extrabold text-on-surface-variant truncate">{{ props.breadcrumb[0] }}</div>
            <span class="text-on-surface-variant/60">&gt;</span>
            <div class="text-xl font-black text-on-surface truncate font-manrope">{{ props.breadcrumb[1] }}</div>
          </template>
        </nav>
      </div>
    </div>

    <div v-if="showBroadcast" class="absolute left-1/2 -translate-x-1/2 max-w-[52vw]">
      <div class="flex items-center justify-center gap-3 bg-white/8 border border-white/10 rounded-full px-4 py-2">
        <span class="w-4 h-4 text-white shrink-0" aria-hidden="true" :style="assetIconStyle('broadcast')" />
        <div class="text-sm font-bold text-on-surface-variant truncate text-center">{{ broadcastText }}</div>
        <button
          v-if="showCallToAction"
          class="shrink-0 bg-white/6 border border-white/10 text-sm font-extrabold text-on-surface px-3 py-1.5 rounded-full hover:bg-white/8 transition-colors"
          type="button"
          @click="onCallToAction"
        >
          {{ callToActionName }}
        </button>
      </div>
    </div>

    <div class="flex items-center gap-4">
      <div class="flex items-center bg-highlight/14 border border-highlight/30 rounded-full p-2 pl-4 gap-4">
        <div class="flex items-center gap-2">
          <template v-if="showWarrantCredits">
            <span class="w-5 h-5 text-white" aria-hidden="true" :style="assetIconStyle('warrant_credits')" />
            <span class="text-sm font-bold text-on-surface">{{ warrantCreditsText }}</span>
            <span class="text-on-surface-variant/60">|</span>
          </template>
          <span class="w-5 h-5 text-white" aria-hidden="true" :style="assetIconStyle('buy_credits')" />
          <span class="text-sm font-bold text-on-surface">{{ buyCreditsText }}</span>
        </div>
        <button class="bg-highlight text-ink text-sm font-black px-4 py-2 rounded-full hover:opacity-90 transition-opacity" type="button" @click="onRecharge">
          优惠充值
        </button>
      </div>
    </div>
  </header>
</template>
